// apps/client/src/App.tsx
//
// Main App component with Telegram Mini App integration
// Includes onboarding & legal gating per PDR
//

import { useEffect, useState, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import { icNoInternet } from './assets';
import { GamePage, HomePage, DebugPage, MissionsPage, ReferralsPage, StorePage } from './pages';
import { SundayGalaPage } from './pages/SundayGalaPage';
import { ProfilePage } from './pages/ProfilePage';
import { OnboardingScreen } from './pages/OnboardingScreen';
import { LegalGatingScreen } from './pages/LegalGatingScreen';
import { PreviewWinPage } from './pages/PreviewWinPage';
// Admin is lazy-loaded to keep it out of the main bundle (~40KB saved)
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
import { useAuthStore } from './store/authStore';
import { useUserStore, checkAndApplyReferralFromUrl } from './store/userStore';
import { useGameStore } from './store/gameStore';
import { api } from './lib/api';
import { Button } from './ui-kit/Button';
import { WelcomeModal } from './ui-kit/WelcomeModal';
import { PrizeModal } from './ui-kit/PrizeModal';
import { useAudio } from './providers/AudioProvider';

// TonConnect manifest URL — served from /public
const manifestUrl = `${window.location.origin}/tonconnect-manifest.json`;

type ViewType = 'home' | 'game' | 'missions' | 'store' | 'referrals' | 'sunday' | 'profile' | 'debug' | 'admin';

function App() {
    const { login, isAuthenticated, isAdmin, token, isLoading } = useAuthStore();
    const { fetchMissions, fetchReferralData, refreshBalance, fetchPublicSettings, hasCompletedOnboarding, hasAcceptedLegal, hasClaimedWelcomeBonus } = useUserStore();
    const startTimerTicks = useGameStore((s) => s.startTimerTicks);
    const [initError, setInitError] = useState<string | null>(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const [currentView, setCurrentView] = useState<ViewType>('home');
    const [storeDefaultTab, setStoreDefaultTab] = useState<'powerups' | 'ofertas'>('powerups');
    const [showWelcome, setShowWelcome] = useState(false);
    const [pendingPrizes, setPendingPrizes] = useState<Array<{
        id: string;
        type: string;
        amount: number;
        description: string | null;
        createdAt: string;
    }>>([]);
    const [showPrizeModal, setShowPrizeModal] = useState(false);
    const { setBgmTheme } = useAudio();

    // Change BGM theme based on current view
    useEffect(() => {
        if (currentView === 'game') {
            setBgmTheme('daily');
        } else if (currentView === 'sunday') {
            setBgmTheme('sunday');
        } else {
            setBgmTheme('lobby');
        }
    }, [currentView, setBgmTheme]);

    useEffect(() => {
        const initApp = async () => {
            try {
                const tg = window.Telegram?.WebApp;

                if (tg && 'initData' in tg) {
                    tg.ready();
                    if ('expand' in tg && typeof tg.expand === 'function') {
                        tg.expand();
                    }

                    const initData = tg.initData as string;

                    if (initData) {
                        console.log('🔐 Authenticating with Telegram...');
                        const success = await login(initData);

                        if (!success) {
                            const authError = useAuthStore.getState().error;
                            console.error('❌ Auth failed:', authError);
                            setInitError(authError || 'Failed to authenticate with Telegram');
                        }
                    } else {
                        console.log('⚠️ No initData available (dev mode?)');
                        setInitError(null);
                    }
                } else {
                    console.log('⚠️ Not running in Telegram WebApp');
                    if (token) {
                        api.setToken(token);
                    }
                }
            } catch (error) {
                console.error('Initialization error:', error);
                setInitError('Failed to initialize app');
            } finally {
                setIsInitializing(false);
            }
        };

        initApp();
    }, [login, token]);

    // Post-auth initialization
    useEffect(() => {
        const postAuthInit = async () => {
            if (!isAuthenticated) return;

            console.log('📱 Post-auth initialization...');

            try {
                await checkAndApplyReferralFromUrl();

                await Promise.all([
                    fetchMissions(),
                    fetchReferralData(),
                    refreshBalance(),
                    fetchPublicSettings(),
                ]);

                // Start dual timers
                startTimerTicks();

                console.log('✅ App fully initialized');
            } catch (error) {
                console.error('Post-auth init error:', error);
            }
        };

        postAuthInit();
    }, [isAuthenticated, fetchMissions, fetchReferralData, refreshBalance, fetchPublicSettings, startTimerTicks]);

    // Show welcome bonus after legal gating is done (if not yet claimed)
    useEffect(() => {
        if (isAuthenticated && hasAcceptedLegal && !hasClaimedWelcomeBonus) {
            setShowWelcome(true);
        }
    }, [isAuthenticated, hasAcceptedLegal, hasClaimedWelcomeBonus]);

    // Check for pending prizes (offline wins) after auth
    useEffect(() => {
        if (!isAuthenticated) return;
        const checkPrizes = async () => {
            try {
                const res = await api.getPendingPrizes();
                if (res.ok && res.prizes.length > 0) {
                    setPendingPrizes(res.prizes);
                    setShowPrizeModal(true);
                }
            } catch {
                // Non-critical — silently ignore
            }
        };
        checkPrizes();
    }, [isAuthenticated]);

    // Navigate handler
    const handleNavigate = (view: ViewType) => {
        // Support "store:ofertas" syntax to navigate to a specific store tab
        if (view.startsWith('store:')) {
            const tab = view.split(':')[1] as 'powerups' | 'ofertas';
            setStoreDefaultTab(tab);
            setCurrentView('store' as ViewType);
        } else {
            if (view === 'store') setStoreDefaultTab('powerups');
            setCurrentView(view);
        }
    };

    // Loading state
    if (isInitializing || isLoading) {
        return (
            <div className="min-h-screen bg-purple-gradient flex items-center justify-center">
                <div className="text-center">
                    <motion.img
                        src="/favicon.png"
                        alt="BingoTON Logo"
                        className="w-24 h-24 mx-auto mb-4 object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]"
                        animate={{ scale: [0.95, 1.05, 0.95] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                    />
                    <div className="text-white font-medium text-3xl mb-2 tracking-wide font-display">BingoTON</div>
                    <div className="text-gray-400 animate-pulse">Loading...</div>
                </div>
            </div>
        );
    }

    // Error state
    if (initError && !isAuthenticated) {
        return (
            <div className="min-h-screen bg-purple-gradient flex items-center justify-center p-4">
                <div className="text-center bg-white/5 border border-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-sm">
                    <img src={icNoInternet} alt="Error" className="w-16 h-16 mx-auto mb-4 opacity-60" />
                    <div className="text-white font-bold text-xl mb-2">Connection Error</div>
                    <div className="text-gray-400 mb-6">{initError}</div>
                    <Button
                        onClick={() => window.location.reload()}
                        variant="primary"
                        fullWidth
                        icon="refresh"
                    >
                        Reintentar
                    </Button>
                </div>
            </div>
        );
    }

    // Debug/Admin/Preview URL params
    const urlParams = new URLSearchParams(window.location.search);
    const isDebugMode = urlParams.has('debug');
    const isAdminMode = urlParams.has('admin');
    const isPreviewWinMode = urlParams.has('preview-win');
    const previewSecret = urlParams.get('secret');

    // Secret route for marketing video captures
    if (isPreviewWinMode && previewSecret === '1234') {
        const amount = Number(urlParams.get('amount')) || 1500;
        const type = urlParams.get('type') || 'BINGO!';
        return <PreviewWinPage amount={amount} title={type} />;
    }

    if (isDebugMode) {
        return <DebugPage />;
    }

    if (isAdminMode && isAdmin) {
        return (
            <Suspense fallback={<div className="min-h-screen bg-[#0a0518] flex items-center justify-center"><div className="text-white/50 animate-pulse">Loading Admin...</div></div>}>
                <AdminPage onBack={() => { window.history.replaceState({}, '', window.location.pathname); setCurrentView('home'); }} />
            </Suspense>
        );
    }

    // --- ONBOARDING GATING ---
    if (isAuthenticated && !hasCompletedOnboarding) {
        return <OnboardingScreen />;
    }

    // --- LEGAL GATING ---
    if (isAuthenticated && !hasAcceptedLegal) {
        return <LegalGatingScreen />;
    }

    // --- MAIN APP ---
    return (
        <>
            {/* Welcome bonus modal */}
            {showWelcome && (
                <WelcomeModal
                    isOpen={showWelcome}
                    onClose={() => setShowWelcome(false)}
                />
            )}

            {/* Prize victory modal (offline wins) */}
            {showPrizeModal && pendingPrizes.length > 0 && (
                <PrizeModal
                    prizes={pendingPrizes}
                    onDismiss={() => {
                        setShowPrizeModal(false);
                        setPendingPrizes([]);
                    }}
                />
            )}

            {/* Router */}
            {(() => {
                switch (currentView) {
                    case 'game':
                        return <GamePage onNavigate={handleNavigate} />;
                    case 'missions':
                        return <MissionsPage onNavigate={handleNavigate} />;
                    case 'referrals':
                        return <ReferralsPage onNavigate={handleNavigate} />;
                    case 'store':
                        return <StorePage onNavigate={handleNavigate} defaultTab={storeDefaultTab} />;
                    case 'sunday':
                        return <SundayGalaPage onNavigate={handleNavigate} />;
                    case 'profile':
                        return <ProfilePage onNavigate={handleNavigate} />;
                    case 'debug':
                        return <DebugPage />;
                    case 'admin':
                        return isAdmin ? (
                            <Suspense fallback={<div className="min-h-screen bg-[#0a0518] flex items-center justify-center"><div className="text-white/50 animate-pulse">Loading Admin...</div></div>}>
                                <AdminPage onBack={() => setCurrentView('home')} />
                            </Suspense>
                        ) : <HomePage onNavigate={handleNavigate} />;
                    case 'home':
                    default:
                        return <HomePage onNavigate={handleNavigate} />;
                }
            })()}
        </>
    );
}

// Wrap with TonConnectUIProvider for wallet integration
function AppWithProviders() {
    return (
        <TonConnectUIProvider manifestUrl={manifestUrl}>
            <App />
        </TonConnectUIProvider>
    );
}

export default AppWithProviders;
